import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { SxProps, useMediaQuery } from '@mui/system';
import { faPen, faEnvelope, faPhone, faPaperPlane } from '@fortawesome/pro-regular-svg-icons';
import { sha256 } from '@europrocurement/l2d-utils';
import { faClose, faLock } from '@fortawesome/pro-duotone-svg-icons';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    useAvatarUrl,
    useAvatarUrlDispatch,
    useModal,
} from '@europrocurement/flexy-components/providers';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import {
    Utilisateur,
    useUtilisateurService,
    ContactTiersRead,
    useTiersServiceSelector,
    selectedPrescripteursSelector,
} from '@europrocurement/l2d-domain';
import { GenericModalMailType } from '@europrocurement/flexy-components/types/GenericModalMailType';
import { ActionsDropdown, CardSection, LabeledIcon, RoleChip } from '../../atoms';
import { ContactActivitiesPreview } from './fragments/ContactActivitiesPreview';
import { CustomAvatar } from '../CustomAvatar';

export type ContactInformationsCurrentUserType = {
    name?: string;
    email: string;
    service?: string;
};

export type ContactInformationsProps = {
    prescriberHasB2BAccess: boolean;
    contact: ContactTiersRead;
    openForm: (c: ContactTiersRead) => void;
    resolveUserInfo: (email: string) => Promise<Utilisateur | null>;
    showMailModal: (p: GenericModalMailType) => void;
    currentUserInfos: ContactInformationsCurrentUserType;
    sx?: SxProps;
    manageAvatar: React.ReactNode | undefined;
    onToggleKeycloakAccess: (
        contact: ContactTiersRead,
        userIdentifier: string | undefined,
    ) => Promise<string | undefined>;
    onSendResetPassword: (contact: ContactTiersRead) => Promise<void>;
};

export const ContactInformations: React.FunctionComponent<ContactInformationsProps> = function ({
    prescriberHasB2BAccess,
    contact,
    openForm,
    sx,
    resolveUserInfo,
    manageAvatar,
    showMailModal,
    currentUserInfos,
    onToggleKeycloakAccess,
    onSendResetPassword,
}) {
    const [isUpdating, setIsUpdating] = useState(false);

    const { isExit } = contact;

    const [userIdentifier, setUserIdentifier] = useState<string | undefined>(
        contact?.userIdentifier,
    );

    const theme = useTheme();
    const { modalActions } = useModal();

    const prescripteur = useTiersServiceSelector(selectedPrescripteursSelector);
    const lgBreakpoint = useMediaQuery(theme.breakpoints.down('lg'));

    const { avatarUrl, usersAvatarInfos } = useAvatarUrl();
    const { avatarBaseUrl } = useUtilisateurService();
    const avatarUrlDispatch = useAvatarUrlDispatch();

    // Gère l'ajout des contacts au context des avatars
    useEffect(() => {
        // Cherche les contacts qui ont un compte Keycloak, si l'identifiant existe, le compte existe
        if (userIdentifier !== undefined && userIdentifier !== null) {
            if (usersAvatarInfos !== undefined) {
                // Cherche si les informations de cet utilisateur sont déjà dans le context avatar
                const inContext = usersAvatarInfos.find(
                    (user) => user.avatarUserIdentifier === userIdentifier,
                );

                // Si le résultat n'apparaît pas dans le find, il faut ajouter les informations de l'utilisateur au context
                if (!inContext) {
                    // hash l'identifiant, formate les données, puis dispatch dans le context
                    sha256(userIdentifier).then((hash) => {
                        // reprend les données existantes et ajoute la nouvelle
                        avatarUrlDispatch({
                            type: 'update',
                            payload: avatarUrl,
                            list: [
                                ...usersAvatarInfos,
                                {
                                    avatarUserIdentifier: userIdentifier,
                                    currentUserAvatarUrl: `${avatarBaseUrl}${hash}?time=${new Date().getTime()}`,
                                },
                            ],
                        });
                    });
                }
            } else {
                // hash l'identifiant, formate les données, puis dispatch dans le context
                sha256(userIdentifier).then((hash) => {
                    // reprend les données existantes et ajoute la nouvelle
                    avatarUrlDispatch({
                        type: 'update',
                        payload: avatarUrl,
                        list: [
                            {
                                avatarUserIdentifier: userIdentifier,
                                currentUserAvatarUrl: `${avatarBaseUrl}${hash}?time=${new Date().getTime()}`,
                            },
                        ],
                    });
                });
            }
        }
    }, [avatarBaseUrl, avatarUrl, avatarUrlDispatch, userIdentifier, usersAvatarInfos]);

    const updateForm = () => openForm(contact);

    const toggleKeycloakAccess = async () => {
        setIsUpdating(true);

        try {
            const newUserIdentifier = await onToggleKeycloakAccess(contact, userIdentifier);

            if (newUserIdentifier !== userIdentifier) {
                setUserIdentifier(newUserIdentifier);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsUpdating(false);
        }
    };

    const sendResetPassword = async () => {
        setIsUpdating(true);

        try {
            await onSendResetPassword(contact);
        } catch (e) {
            console.error(e);
        } finally {
            setIsUpdating(false);
        }
    };

    const accessActions = [
        {
            icon: faLock,
            label: userIdentifier ? "Désactiver l'accès" : "Activer l'accès",
            disabled: isUpdating,
            onClick: toggleKeycloakAccess,
        },
    ];

    if (userIdentifier) {
        accessActions.push({
            icon: faPaperPlane,
            label: "Renvoyer l'email de connexion",
            disabled: isUpdating,
            onClick: sendResetPassword,
        });
    }

    const actions = [
        { icon: faPen, label: 'Éditer le contact', disabled: isUpdating, onClick: updateForm },
    ];

    // ouvre une modale avec le composant de gestion des avatars (ManageAvatar)
    // n'est appelé que si l'action a été ajoutée à la liste (condition ci-dessous)
    const manageAvatarModal = () => {
        modalActions.call(manageAvatar);
    };

    // ajoute aux actions la gestion de l'avatar seulement s'il y a un identifiant keycloak pour le contact (ne vaut ni null ni undefined).
    if (manageAvatar !== undefined && userIdentifier) {
        actions.push({
            icon: faUser,
            label: "Editer l'avatar",
            disabled: isUpdating,
            onClick: manageAvatarModal,
        });
    }

    if (prescriberHasB2BAccess && !isExit) {
        accessActions.forEach((action) => actions.push(action));
    }

    const fullName = `${contact.prenomContact} ${contact.nomContact}`;
    const mail = contact.emailsContact[0]?.email;
    const phone = contact.telephonesContact[0]?.telephone;
    const isAdmin = contact.administrateur;
    const isBilling = contact.comptabilite;
    const isMain = contact.principal;

    const avatarSize = 40;
    const avatarStyle = {
        width: avatarSize,
        height: avatarSize,
        marginTop: '10px',
        marginRight: '15px',
        borderRadius: '40px',
    };

    const deletedIcon = (
        <FaOptionIcon
            sx={{ avatarStyle, fontSize: '30px' }}
            icon={faClose}
        />
    );

    // Pour mettre à jour l'image d'avatar du contact, il faut passer en props l'url via src (la source prend le dessus sur le keycloakId pour le rendu)
    const avatar = (
        <CustomAvatar
            firstName={contact.prenomContact}
            lastName={contact.nomContact}
            src={
                usersAvatarInfos?.find((user) => user.avatarUserIdentifier === userIdentifier)
                    ?.currentUserAvatarUrl
            }
            keycloakId={userIdentifier}
            sx={avatarStyle}
        />
    );

    let mailBodyContent = `Bonjour ${contact.prenomContact}  ${contact.nomContact}\n\n\n\n`;
    mailBodyContent += `Cordialement,\n${currentUserInfos.name}${currentUserInfos.service && `\n${currentUserInfos.service}`}\n${currentUserInfos.email}`;

    const handleOpenModalSendMail = () => {
        if (prescripteur) {
            let codeClient = '';
            if (prescripteur.relationsSocietes.length > 0) {
                codeClient = `#${prescripteur.relationsSocietes[0].codeClient}` || '';
            }

            showMailModal({
                to: [
                    { email: mail, firstName: contact.prenomContact, lastName: contact.nomContact },
                ],
                subject: `${prescripteur?.raisonSociale} ${codeClient}`,
                body: mailBodyContent,
                prescriber: prescripteur,
                showBody: true,
                showAttachments: true,
            });
        }
    };

    return (
        <CardSection
            header={null}
            sx={{
                display: 'flex',
                gap: '8px',
                padding: '8px',
                border: '1px solid lightgray',
                marginY: 0,
                maxWidth: `calc(${lgBreakpoint ? '100%' : '50%'} - 8px)`,
                width: '100%',
                overflow: 'hidden',
                backgroundColor: isExit ? 'transparent' : theme.palette.background.paper,
                ...sx,
            }}
            sxContent={{ padding: 0 }}
        >
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    minHeight: '180px',
                    height: '100%',
                    ...(isExit && {
                        opacity: 0.5,
                    }),
                }}
            >
                <Box sx={{ width: '56px' }}>{isExit ? deletedIcon : avatar}</Box>

                <Box
                    sx={{
                        width: '100%',
                        gap: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <Typography variant="l2dh7">{fullName}</Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {isMain && <RoleChip label="Principal" />}
                                    {isBilling && <RoleChip label="Comptabilité" />}
                                    {isAdmin && <RoleChip label="Administrateur" />}
                                </Box>
                            </Box>
                            <Box sx={{ paddingX: '8px' }}>
                                <ActionsDropdown actions={actions} />
                            </Box>
                        </Box>

                        <Typography
                            variant="l2dbodyxs"
                            sx={{ color: 'text.gris70' }}
                        >
                            {contact.fonction}
                        </Typography>
                    </Box>

                    <Box>
                        <Box
                            onClick={handleOpenModalSendMail}
                            sx={{ cursor: 'pointer' }}
                        >
                            <LabeledIcon
                                icon={faEnvelope}
                                label={mail || '-'}
                                labelVariant="l2dbodyxs"
                            />
                        </Box>
                        <LabeledIcon
                            icon={faPhone}
                            label={phone || '-'}
                            labelVariant="l2dbodyxs"
                        />
                    </Box>

                    {/* Section notes read-only */}
                    <ContactActivitiesPreview
                        contact={contact}
                        resolveUserInfo={resolveUserInfo}
                    />
                </Box>
            </Box>
        </CardSection>
    );
};
